import Vue from 'vue';
import Router from 'vue-router';
import store from '../store/store';
import Login from '../views/Login.vue';
import ErrorNotFound from '../views/ErrorNotFound.vue';
import UserLayout from '../layouts/UserLayout.vue';
import Terms from '@/views/Terms.vue';
import PrivacyPolicy from '@/views/PrivacyPolicy.vue';

Vue.use(Router);

const routes = [
  {
    path: '/',
    redirect: '/login',
  },
  {
    path: '/login',
    name: 'LoginPage',
    component: Login,
    beforeEnter: (to, from, next) => {
      if (store.getters.isLoggedIn) {
        next('/user/dashboard');
      } else {
        next();
      }
    },
  },
  {
    path: '/signup',
    name: 'Signup',
    component: () => import('@/views/SignupPage.vue')
  },
  {
    path: '/auth',
    name: 'Auth',
    component: () => import('@/views/Auth.vue'),
    props: (route) => ({ token: route.query.token })
  },
  {
    path: '/invoice/view/:token',
    name: 'PublicInvoiceView',
    component: () => import('@/views/PublicInvoiceView.vue'),
    meta: { public: true } // if you’re using route guards
  },
  {
    path: '/user',
    component: UserLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: 'dashboard',
        name: 'DashboardPage',
        component: () => import('../views/Dashboard.vue'),
      },
      {
        path: 'clients',
        name: 'ClientsPage',
        component: () => import('../views/Clients.vue'),
        meta: { title: 'Clients Management' },
      },
      {
        path: 'invoices',
        name: 'InvoicesPage',
        component: () => import('../views/Invoices.vue'),
        meta: { title: 'Invoice Management' },
      },
      {
        path: 'payments',
        name: 'PaymentsPage',
        component: () => import('../views/Payments.vue'),
        meta: { title: 'Payments Tracking' },
      },
      {
        path: 'payments/pending',
        name: 'PaymentsPendingPage',
        component: () => import('../views/PaymentsPending.vue'),
        meta: { title: 'Pending Payments Tracking' },
      },
      {
        path: 'account',
        name: 'AccountPage',
        component: () => import('../views/Account.vue'),
        meta: { title: 'Subscription Account' },
      },
      {
        path: 'company',
        name: 'CompanyPage',
        component: () => import('../views/Company.vue'),
        meta: { title: 'Company Settings' },
      },
      {
        path: 'email-settings',
        name: 'EmailSettingsPage',
        component: () => import('../views/EmailSettings.vue'),
        meta: { title: 'Email Settings' },
      },
      {
        path: 'payment-settings',
        name: 'PaymentSettingsPage',
        component: () => import('../views/PaymentSettings.vue'),
        meta: { title: 'Payment Settings' },
      },
      {
        path: 'email-templates',
        name: 'EmailTemplatesPage',
        component: () => import('../views/EmailTemplates.vue'),
        meta: { title: 'Email Templates' },
      },
      {
        path: 'profile',
        name: 'ProfilePage',
        component: () => import('../views/Profile.vue'),
        meta: { title: 'User Profile' },
      },
    ],
  },
  { path: '/terms', name: 'Terms', component: Terms },
  { path: '/policy', name: 'PrivacyPolicy', component: PrivacyPolicy },
  {
    path: '/:catchAll(.*)*',
    meta: { title: 'Page Not Found' },
    beforeEnter: (to, from, next) => {
      // Skip if it's an API route
      if (to.path.startsWith('/api')) {
        return next(false); // Let the server handle it
      }
      next();
    },
    component: ErrorNotFound
  }
];

const router = new Router({
  routes,
  mode: 'history',
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { x: 0, y: 0 };
  },
});

router.beforeEach((to, from, next) => {
  const isLoggedIn = store.getters.isLoggedIn;
  document.title = to.meta.title ? `${to.meta.title} | ${process.env.VUE_APP_NAME}` : process.env.VUE_APP_NAME || 'App';

  if (to.path === from.path) {
    return next();
  }

  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!isLoggedIn) {
      next({
        path: '/login',
        query: { redirect: to.fullPath },
      });
    } else {
      next();
    }
  } else {
    next();
  }
});

router.onError((error) => {
  console.error('Router Error:', error);
});

export default router;